import { GetRowIdFunc, GridOptions, GridReadyEvent } from '@ag-grid-community/core'
import { CloudDownloadRounded, CloudUploadRounded } from '@mui/icons-material'
import { Box, CircularProgress, ClassNameMap, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Popover from '@mui/material/Popover'
import { makeStyles } from '@mui/styles'
import { TableDataResponse } from 'genesis-suite/components/tables/VirtualTable/VirtualTable'
import { AddUser, Save, Trash } from 'genesis-suite/icons'
import { ExportTypes } from 'genesis-suite/types/visualTypes'
import { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react'
import userRoles from '../../../lib/userRoles'
import AgDataTable from '../../ag-data-table/DataTable'
import { buildXGridColumns } from './userTableUtils'
import { FilterList } from '@mui/icons-material'
import { DomainContext } from '../../contexts/DomainContext'

interface FormTableProps extends Partial<GridOptions> {
    context: any
    selectedUser: { userId: string[] }
    onChange: (user: any) => void
    onSubmit: () => void
    toggleUploadFile: () => void
    onUserCheckbox: (userIds: any[]) => void
    onUserFilterSelect: (user: any) => void
    buttonState: any
    selectedUserList: any[]
    onShowAddUserModal: () => void
    onDeleteUsers: () => void
    onRequestRows?: (page: number | undefined, pageSize: number) => Promise<TableDataResponse>
    className: ClassNameMap<'container' | 'summary' | 'tableContainer' | 'addUserFab' | 'filterWrap' | 'DZPaper'>
    onGridReady?: (params: GridReadyEvent) => void
    handleSearch: (searchText: string) => void
    exportUsers: (exportType: ExportTypes) => void
    gridStatus: string
    getRowId: GetRowIdFunc
    onShowNetworkFiltersModal?: () => void
}
const useStyles = makeStyles((theme: any) => ({
    table: {
        position: 'relative',
        display: 'flex',
        flex: '1 0 auto',
        flexFlow: 'column nowrap',
        paddingRight: 1,
        [theme.breakpoints.down('md')]: {
            margin: '.5rem auto 0 auto',
        },
        '& .filter-disabled': {
            flex: '0 0 auto',
            width: 60,
            textAlign: 'center',
            backgroundColor: '#e0e0e0',
            opacity: '.65',
            cursor: 'not-allowed',
        },
    },
    actionButton: {
        marginLeft: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
        },
        '&$disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
        },
    },
    disabled: {},
    submitSpinner: {
        color: theme.palette.primary.light,
        position: 'relative',
        zIndex: 9,
    },
    showPointer: {
        cursor: 'pointer',
    },
}))

const UserTable = forwardRef(
    (
        {
            context,
            onRequestRows,
            className,
            onShowAddUserModal,
            onSubmit,
            buttonState,
            onDeleteUsers,
            selectedUserList,
            onUserCheckbox,
            toggleUploadFile,
            onUserFilterSelect,
            onChange,
            selectedUser,
            onGridReady,
            handleSearch,
            exportUsers,
            gridStatus,
            getRowId,
            onShowNetworkFiltersModal,
        }: FormTableProps,
        ref
    ) => {
        const classes = useStyles()
        const theme = useTheme()
        const {
            palette: {
                primary: { contrastText },
            },
        } = theme

        const [anchorEl, setAnchorEl] = useState(null)

        const dataTableRef = useRef(null)

        const userExportId = Boolean(anchorEl) == true ? 'user-export' : undefined

        const { companies, domains } = useContext(DomainContext)

        const handleExportBtnClick = event => {
            setAnchorEl(event.currentTarget)
        }

        const getUsers = () => {
            return dataTableRef?.current?.getUsers()
        }

        const handleExportPopoverClose = () => {
            setAnchorEl(null)
        }

        const updateUser = user => {
            dataTableRef?.current?.updateUser(user)
        }

        useImperativeHandle(ref, () => ({
            handleExportPopoverClose,
            getUsers,
            updateUser,
        }))

        const handleChange = ({ oldValue, newValue, data, column, colDef }: any) => {
            if (newValue === oldValue || (newValue !== 0 && !newValue && !oldValue)) return
            const users = getUsers()
            const user = users?.find((user: any) => user.data.id === data.id)

            if (!user || !user.data) return // User not found, skip
            const userData = user.data

            if (column.colId === 'role') {
                const val = Object.keys(userRoles).find(
                    (key: any) => userRoles[key as keyof typeof userRoles]?.toLowerCase() === newValue?.toLowerCase()
                )

                userData.permissions = { ...userData.permissions, [context]: val }
                userData.role = newValue
            } else if (column.colId === `scenarioPublishPermissions.${context}`) {
                userData.scenarioPublishPermissions = { ...userData.scenarioPublishPermissions, [context]: newValue }
            } else if (column.colId === `scenarioApprovalPermissions.${context}`) {
                userData.scenarioApprovalPermissions = { ...userData.scenarioApprovalPermissions, [context]: newValue }
            } else {
                userData[column.colId] = newValue
            }

            onChange?.(userData)
        }

        const addUser = (
            <Tooltip title="Add User">
                <IconButton
                    key="add-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    onClick={onShowAddUserModal}
                    size="large"
                >
                    <AddUser htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )

        const saveBtn = (
            <Tooltip title="Save Changes">
                <IconButton
                    key="save-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    disabled={buttonState.disabled}
                    onClick={onSubmit}
                    size="large"
                >
                    {!buttonState.submitting ? (
                        <Save htmlColor={contrastText} />
                    ) : (
                        <CircularProgress size={24} className={classes.submitSpinner} />
                    )}
                </IconButton>
            </Tooltip>
        )

        const deleteUsers = (
            <Tooltip title="Delete">
                <IconButton
                    key="delete-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    disabled={selectedUserList.length === 0}
                    onClick={onDeleteUsers}
                    size="large"
                >
                    <Trash htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )

        const uploadFile = (
            <Tooltip title="Add Multiple users">
                <IconButton
                    key="upload-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    onClick={toggleUploadFile}
                    size="large"
                >
                    <CloudUploadRounded htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )

        const downloadUsers = (
            <Tooltip title="Export users">
                <IconButton
                    key="upload-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    onClick={handleExportBtnClick}
                    size="large"
                >
                    <CloudDownloadRounded htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )
        const networkFiltersPermission = (
            <Tooltip title="Network Filters">
                <IconButton
                    aria-label="Open network filters"
                    key="networkFilters-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    disabled={selectedUserList.length === 0 || selectedUserList.length >= 2}
                    onClick={onShowNetworkFiltersModal}
                >
                    <FilterList htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )

        return (
            <Box sx={{ width: '100%', height: '100%' }}>
                <AgDataTable
                    ref={dataTableRef}
                    columns={buildXGridColumns(onUserFilterSelect, selectedUser, classes, theme, context)}
                    onGridReady={onGridReady}
                    extraActions={[addUser, saveBtn, networkFiltersPermission, deleteUsers, uploadFile, downloadUsers]}
                    onRowSelected={onUserCheckbox}
                    title="Manage Users"
                    handleCellEdit={handleChange}
                    handleSearch={handleSearch}
                    gridStatus={gridStatus}
                    getRowId={getRowId}
                />
                <Popover
                    id={userExportId}
                    open={Boolean(userExportId)}
                    anchorEl={anchorEl}
                    onClose={handleExportPopoverClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuList>
                        <MenuItem onClick={() => exportUsers(ExportTypes.Excel)}>
                            <Typography>Export as Excel</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => exportUsers(ExportTypes.PDF)}>
                            <Typography>Export as PDF</Typography>
                        </MenuItem>
                    </MenuList>
                </Popover>
            </Box>
        )
    }
)
export default UserTable
